import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  alpha,
  useTheme,
  Paper,
  Avatar,
} from '@mui/material';
import {
  SmartToy,
  Person,
  TrendingUp,
  Security,
  Speed,
  CheckCircle,
  PlayArrow,
  Notifications,
  Assessment,
  ThumbUp,
  ThumbDown,
  Edit,
} from '@mui/icons-material';

interface HITLIntroductionStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  updateData: (data: any) => void;
}

const HITLIntroductionStep: React.FC<HITLIntroductionStepProps> = ({
  onNext,
  data,
}) => {
  const theme = useTheme();
  const [activeDemo, setActiveDemo] = useState(0);
  const [understood, setUnderstood] = useState(false);

  const hitlWorkflow = [
    {
      label: 'AI Analyzes',
      description: 'AI agents continuously monitor your supply chain data',
      icon: <SmartToy />,
      details: [
        'Real-time data processing',
        'Pattern recognition',
        'Anomaly detection',
        'Predictive analytics',
      ],
    },
    {
      label: 'AI Recommends',
      description: 'Agents generate insights and recommendations',
      icon: <Assessment />,
      details: [
        'Data-driven suggestions',
        'Multiple scenarios',
        'Risk assessment',
        'Cost-benefit analysis',
      ],
    },
    {
      label: 'You Review',
      description: 'You receive notifications and review recommendations',
      icon: <Person />,
      details: [
        'Clear explanations',
        'Supporting evidence',
        'Alternative options',
        'Impact predictions',
      ],
    },
    {
      label: 'You Decide',
      description: 'You make the final decision with full context',
      icon: <ThumbUp />,
      details: [
        'Approve recommendations',
        'Request modifications',
        'Reject with feedback',
        'Ask for more analysis',
      ],
    },
    {
      label: 'AI Learns',
      description: 'System learns from your decisions to improve',
      icon: <TrendingUp />,
      details: [
        'Preference learning',
        'Better recommendations',
        'Personalized insights',
        'Continuous improvement',
      ],
    },
  ];

  const benefits = [
    {
      icon: <Security />,
      title: 'You Stay in Control',
      description: 'AI never makes decisions for you - you always have the final say',
      color: theme.palette.primary.main,
    },
    {
      icon: <Speed />,
      title: 'Faster Decisions',
      description: 'AI does the heavy lifting of data analysis, you focus on strategy',
      color: theme.palette.secondary.main,
    },
    {
      icon: <TrendingUp />,
      title: 'Better Outcomes',
      description: 'Combine AI insights with your expertise for optimal results',
      color: theme.palette.success.main,
    },
    {
      icon: <SmartToy />,
      title: 'Continuous Learning',
      description: 'System gets smarter over time by learning from your decisions',
      color: theme.palette.info.main,
    },
  ];

  const exampleScenario = {
    situation: 'Supplier Disruption Alert',
    aiAnalysis: 'Primary supplier (60% of electronics) experiencing 4-week delay',
    aiRecommendation: 'Switch to alternative supplier with 2-week expedited shipping',
    yourOptions: [
      { action: 'Approve', impact: 'Higher cost but maintains schedule' },
      { action: 'Modify', impact: 'Partial switch to reduce cost' },
      { action: 'Reject', impact: 'Provide alternative strategy' },
    ],
  };

  const handleNext = () => {
    onNext({ hitlUnderstood: true, completedDemo: true });
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            mx: 'auto',
            mb: 2,
          }}
        >
          <SmartToy sx={{ fontSize: 32 }} />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Human-in-the-Loop AI
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Understanding how you'll work with AI agents
        </Typography>
      </Box>

      {/* Core Concept */}
      <Card sx={{ mb: 4, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            The ASCIN Philosophy
          </Typography>
          <Typography variant="body1" paragraph>
            ASCIN uses <strong>Human-in-the-Loop (HITL)</strong> AI, which means you're
            always in control. Our AI agents are your intelligent assistants, not
            autonomous decision-makers.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
              <SmartToy />
            </Avatar>
            <Typography variant="body2">
              <strong>AI analyzes</strong> vast amounts of data and identifies patterns
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
              <Person />
            </Avatar>
            <Typography variant="body2">
              <strong>You decide</strong> based on AI insights and your expertise
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Workflow Stepper */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            How It Works
          </Typography>
          <Stepper activeStep={activeDemo} orientation="vertical">
            {hitlWorkflow.map((step, index) => (
              <Step key={step.label} expanded>
                <StepLabel
                  StepIconComponent={() => (
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor:
                          index <= activeDemo
                            ? theme.palette.primary.main
                            : theme.palette.grey[300],
                      }}
                    >
                      {step.icon}
                    </Avatar>
                  )}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {step.description}
                  </Typography>
                  <List dense>
                    {step.details.map((detail, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon>
                          <CheckCircle
                            sx={{ fontSize: 20, color: theme.palette.success.main }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={detail}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  {index < hitlWorkflow.length - 1 && (
                    <Button
                      size="small"
                      onClick={() => setActiveDemo(index + 1)}
                      sx={{ mt: 1 }}
                    >
                      Next Step
                    </Button>
                  )}
                </StepContent>
              </Step>
            ))}
          </Stepper>
          {activeDemo === hitlWorkflow.length - 1 && (
            <Button
              variant="outlined"
              onClick={() => setActiveDemo(0)}
              sx={{ mt: 2 }}
            >
              Restart Demo
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Example Scenario */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Real-World Example
          </Typography>
          
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {exampleScenario.situation}
            </Typography>
          </Alert>

          <Paper sx={{ p: 2, mb: 2, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              🤖 AI Analysis
            </Typography>
            <Typography variant="body2">{exampleScenario.aiAnalysis}</Typography>
          </Paper>

          <Paper sx={{ p: 2, mb: 2, bgcolor: alpha(theme.palette.success.main, 0.05) }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              💡 AI Recommendation
            </Typography>
            <Typography variant="body2">{exampleScenario.aiRecommendation}</Typography>
          </Paper>

          <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              👤 Your Options
            </Typography>
            <Grid container spacing={2}>
              {exampleScenario.yourOptions.map((option, index) => (
                <Grid item xs={12} key={index}>
                  <Box
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {option.action}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.impact}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </CardContent>
      </Card>

      {/* Benefits Grid */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Why Human-in-the-Loop?
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {benefits.map((benefit, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(benefit.color, 0.1),
                      color: benefit.color,
                      mr: 2,
                    }}
                  >
                    {benefit.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {benefit.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {benefit.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* What You'll See */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            What You'll See in Your Dashboard
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <Notifications color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Task Notifications"
                secondary="Get alerted when AI agents need your input or approval"
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <Assessment color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Detailed Analysis"
                secondary="View AI reasoning, data sources, and confidence levels"
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <ThumbUp color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Decision Controls"
                secondary="Approve, modify, or reject recommendations with one click"
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <Edit color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Feedback Loop"
                secondary="Provide feedback to help AI learn your preferences"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Confirmation */}
      <Card
        sx={{
          bgcolor: understood
            ? alpha(theme.palette.success.main, 0.1)
            : alpha(theme.palette.grey[500], 0.05),
          border: 2,
          borderColor: understood ? theme.palette.success.main : 'divider',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {understood ? (
              <CheckCircle sx={{ color: theme.palette.success.main, mr: 1 }} />
            ) : (
              <PlayArrow sx={{ color: theme.palette.grey[500], mr: 1 }} />
            )}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Ready to Continue?
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            By continuing, you confirm that you understand:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="AI agents provide recommendations, not autonomous decisions"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="You review and approve all significant actions"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Your feedback helps the system learn and improve"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          </List>
          <Button
            variant="contained"
            fullWidth
            onClick={() => setUnderstood(true)}
            disabled={understood}
            sx={{ mt: 2 }}
          >
            {understood ? 'Understanding Confirmed ✓' : 'I Understand'}
          </Button>
        </CardContent>
      </Card>

      {/* Hidden submit */}
      <Box sx={{ display: 'none' }}>
        <button onClick={handleNext} disabled={!understood}>
          Submit
        </button>
      </Box>
    </Box>
  );
};

export default HITLIntroductionStep;
